import { captureException } from '@sentry/react';
import Loader from 'Components/Forms/Loader';
import useWindowFocus from 'hooks/useWindowFocus';
import { AlertDialog } from 'DesignSystem';
import { secureCachePersistor } from 'graphql/secure/SecureApolloProvider';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import useLocalStorage from 'react-use/lib/useLocalStorage';
import { useFirebase } from '../FeatureModules/Auth/Contexts/FirebaseContext';

const STORAGE_KEY = 'whitelabel-cms-version';
const VersionContext = createContext<null | string>(null);

interface IVersionData {
  version: string;
  releaseDate: string;
  commitHash: string;
  environment: string;
  buildTimestamp: string;
}

/**
 * Unified version checking provider that:
 * 1. Checks version on app initialization (replaces VersionContext)
 * 2. Re-checks version on window focus (replaces FrontendVersionContext)
 * 3. Uses static version.json file instead of GraphQL backend
 */
const VersionProvider: FC<PropsWithChildren> = ({ children }) => {
  const { authenticated, logout } = useFirebase();

  // Version check states
  const [didCheckVersion, setDidCheckVersion] = useState(false);
  const [versionIsOutdated, setVersionIsOutdated] = useState(false);
  const [checkedOnce, setCheckedOnce] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);

  const [storedVersion, setStoredVersion] = useLocalStorage<string>(STORAGE_KEY);
  const focused = useWindowFocus();

  const avoidVersionCheck = process.env.NODE_ENV === 'development';

  /**
   * Fetch version.json from static hosting
   * Returns null on error (silent failure)
   */
  const fetchVersion = async (): Promise<IVersionData | null> => {
    try {
      const response = await fetch('/version.json', {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        console.warn(`Failed to fetch version.json: ${response.status} ${response.statusText}`);
        return null;
      }

      const data: IVersionData = await response.json();
      return data;
    } catch (error) {
      captureException(error);
      return null;
    }
  };

  /**
   * Initial version check on app load
   * If version mismatch detected, logs out user and clears cache
   */
  const checkVersionOnInit = useCallback(async () => {
    if (avoidVersionCheck) {
      setDidCheckVersion(true);
      return;
    }

    const versionData = await fetchVersion();

    if (!versionData) {
      // Silent failure - allow app to continue
      setDidCheckVersion(true);
      return;
    }

    const fetchedVersion = versionData.commitHash;
    setCurrentVersion(fetchedVersion);

    // Store version if none exists or user not authenticated yet
    if (!storedVersion || !authenticated) {
      setStoredVersion(fetchedVersion);
      setDidCheckVersion(true);
      return;
    }

    // Version mismatch - force logout and cache clear
    if (storedVersion !== fetchedVersion && authenticated) {
      setStoredVersion(fetchedVersion);
      await secureCachePersistor.purge();
      await logout();
    }

    setDidCheckVersion(true);
  }, [avoidVersionCheck, authenticated, storedVersion, setStoredVersion, logout]);

  /**
   * Re-check version on window focus
   * Shows alert dialog if new version detected
   */
  const checkVersionOnFocus = useCallback(async () => {
    if (avoidVersionCheck || !focused || !checkedOnce) {
      return;
    }

    const versionData = await fetchVersion();

    if (!versionData) {
      // Silent failure - don't interrupt user
      return;
    }

    const fetchedVersion = versionData.commitHash;
    setCurrentVersion(fetchedVersion);

    // Version changed while user had tab open
    if (storedVersion && fetchedVersion !== storedVersion) {
      setStoredVersion(fetchedVersion);
      setVersionIsOutdated(true);
    }
  }, [avoidVersionCheck, focused, checkedOnce, storedVersion, setStoredVersion]);

  // Initial version check on mount
  useEffect(() => {
    checkVersionOnInit();
  }, [checkVersionOnInit]);

  // Mark as checked once after first load
  useEffect(() => {
    if (didCheckVersion && !checkedOnce) {
      setCheckedOnce(true);
    }
  }, [didCheckVersion, checkedOnce]);

  // Re-check on window focus
  useEffect(() => {
    if (focused && checkedOnce) {
      checkVersionOnFocus();
    }
  }, [focused, checkedOnce, checkVersionOnFocus]);

  /**
   * Handle user clicking refresh button in alert dialog
   * Clears all caches and reloads the page
   */
  const handleClickRefresh = async () => {
    await secureCachePersistor.purge();
    await logout();
    window.location.reload();
  };

  // Block rendering until initial version check completes
  if (!didCheckVersion) {
    return <Loader />;
  }

  return (
    <VersionContext.Provider value={currentVersion}>
      <AlertDialog
        title="A newer version has been detected"
        open={versionIsOutdated}
        text="We have released a new version of the app. Please reload to get all the new goodness!"
        onSubmit={handleClickRefresh}
        submitText="Refresh"
        onCancel={
          process.env.NODE_ENV === 'development' ? () => setVersionIsOutdated(false) : undefined
        }
      />
      {children}
    </VersionContext.Provider>
  );
};

export { VersionContext, VersionProvider };

export default VersionContext;
